import { Avatar, Box, Button, Typography, useTheme } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { colorTheme } from 'theme';
import { MuiOtpInput } from 'mui-one-time-password-input';
import { useDispatch } from 'react-redux';
import {
  setEmailToken,
  setEmailVerified,
  setVerifiedEmail,
  setFirstName,
  setLastName,
  setEmpId,
  setOrgId,
  setProfileIamge,
} from 'store/onboarding';
import { setSnackBarSuccess, setSnackBarFailed } from 'store/snackBar';
import LoadingButton from '@mui/lab/LoadingButton';
import { strings } from 'i18n/Strings';
import { RouteList } from '../../../routes/Constant';
import Logo from '../../../assets/images/Logo100X132.png';
import {
  sendOtp,
  verifyOtp,
  emailDetails,
  loginCheck,
  getRole,
} from '../../../service/AuthService';
import { type IErrorResponse } from 'types';
import { setIsLoggedIn, setRole, setUserDetails } from 'store/auth';

export interface ResponseData {
  result: {
    firstName: string;
    lastName: string;
    empId: number;
    organizationId: number;
    profileImage: string;
  };
  token: string;
}

const VerifyOtp = (): JSX.Element => {
  const MAX_CODE_LENGTH = 4;
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [emailCode, setEmailCode] = useState('');
  const [errorEmail, setErrorEmail] = useState('');
  const [counter, setCounter] = useState(59);
  const [loader, setLoader] = useState<boolean>(false);

  const email = state.email.toLowerCase();
  const token = state.token;

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (counter > 0) {
      timer = setTimeout(() => {
        setCounter(counter - 1);
      }, 1000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [counter]);

  const matchIsNumeric = (character: string): boolean => {
    const isNumber = typeof character === 'number';
    return isNumber || !isNaN(Number(character));
  };

  const validateChar = (value: string): boolean => {
    if (!matchIsNumeric(value)) {
      setErrorEmail(strings.displayText.allowDigitsOnlyText);
      return false;
    } else {
      setErrorEmail('');
      return true;
    }
  };

  const handleOtp = (userOtp: string): void => {
    setEmailCode(userOtp);
  };

  const handleEmailDetails = (email: string): void => {
    void (async () => {
      await emailDetails(email)
        .then((data: unknown) => {
          const responseData = data as ResponseData;
          dispatch(setFirstName(responseData?.result?.firstName));
          dispatch(setLastName(responseData?.result?.lastName));
          dispatch(setEmpId(responseData?.result?.empId));
          dispatch(setOrgId(responseData?.result?.organizationId));
          dispatch(setProfileIamge(responseData?.result?.profileImage));
          dispatch(setUserDetails(responseData?.result));
        })
        .catch((err: IErrorResponse) => {
          dispatch(
            setSnackBarFailed({ snackBarMessage: err.response.data.message }),
          );
        });
    })();
  };

  const handleVerifyOtp = (): void => {
    if (email !== '') {
      if (emailCode !== '') {
        setLoader(true);
        verifyOtp(email, 'email', '' + emailCode, token)
          .then((otpEmailVerify: { token: string } | string | undefined) => {
            setLoader(false);
            setErrorEmail('');
            if (typeof otpEmailVerify !== 'string' && otpEmailVerify?.token) {
              dispatch(setEmailToken(otpEmailVerify.token));
            }
            dispatch(setEmailVerified(true));
            dispatch(setVerifiedEmail(email));
            handleEmailDetails(email);
            dispatch(
              setSnackBarSuccess({
                snackBarMessage: strings.displayText.otpVerifiedSuccessText,
              }),
            );
            const credentials = JSON.parse(
              sessionStorage.getItem('credentials') as string,
            );
            dispatch(setIsLoggedIn(loginCheck()));
            dispatch(setRole(getRole()));
            credentials?.access_token && navigate(RouteList.Home);
          })
          .catch((err) => {
            dispatch(
              setSnackBarFailed({
                snackBarMessage: err?.response?.data?.message,
              }),
            );
            if (err?.response?.status === 400) {
              setErrorEmail(err?.response?.data?.message);
              setLoader(false);
              dispatch(setEmailVerified(false));
            } else {
              setLoader(false);
              setErrorEmail(strings.displayText.unknownErrorText);
              dispatch(setEmailVerified(false));
            }
          });
      } else {
        setErrorEmail('Enter the code');
      }
    }
  };

  const handleResendButton = (): void => {
    try {
      if (email !== '') {
        setEmailCode('');
        setErrorEmail('');
        setCounter(60);
        dispatch(setEmailVerified(false));
        void sendOtp(email, 'email', token);
        dispatch(
          setSnackBarSuccess({
            snackBarMessage: strings.displayText.otpSentSuccessText,
          }),
        );
      }
    } catch (error) {
      dispatch(setSnackBarFailed({ snackBarMessage: error as string }));
    }
  };

  const styles = {
    mainBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: 350,
      marginX: 'auto',
    },
    avatar: {
      width: 100,
      height: 100,
      p: 2,
      bgcolor: colors.primaryBg[900],
      boxShadow: 3,
      position: 'relative',
      top: -48,
    },
    avatarImg: {
      width: 46,
      height: 56,
    },
    otpTypho: {
      fontFamily: 'Source Sans Pro !important',
      fontWeight: 700,
      color: colors.greenAccent[500],
    },
    pleaseEnterOtpTypho: {
      color: colors.gray[500],
      fontWeight: 400,
      fontSize: 14,
      lineHeight: 2,
    },
    errorEmail: {
      color: 'red',
      fontWeight: 400,
      fontSize: 14,
    },
    didNotReceiveMainBox: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    didnotReceiveTypho: {
      fontFamily: 'Inter !important',
      fontWeight: 400,
      fontSize: 12,
    },
    resendOtpTypho: {
      fontWeight: 400,
      fontSize: 12,
    },
    loadingBtn: {
      mt: 3,
      mb: 2,
      bgcolor: colors.greenAccent[500],
      borderRadius: 2,
      py: 1.5,
    },
  };

  return (
    <Fragment>
      <Box sx={styles.mainBox}>
        <Avatar alt="Zen Pensions logo" sx={styles.avatar}>
          <img style={styles.avatarImg} src={Logo} />
        </Avatar>
        <Typography component="h1" variant="h4" sx={styles.otpTypho}>
          {strings.displayText.weHaveSentOtp}
        </Typography>
        <Typography component="h1" variant="h4" sx={styles.otpTypho}>
          {email}
        </Typography>

        <Typography component="h5" variant="h5" sx={styles.pleaseEnterOtpTypho}>
          {strings.displayText.pleaseEnterOtp}
        </Typography>
        <Box pb={5} />
        <MuiOtpInput
          TextFieldsProps={{ autoComplete: 'off' }}
          value={emailCode}
          onChange={handleOtp}
          length={MAX_CODE_LENGTH}
          validateChar={validateChar}
        />
        <Box pb={2} />
        {errorEmail.length > 0 && (
          <Typography component="h5" variant="h5" sx={styles.errorEmail}>
            {errorEmail}
          </Typography>
        )}
        <Box pb={5} />
        <Box sx={styles.didNotReceiveMainBox}>
          <Typography
            component="h5"
            variant="h5"
            sx={styles.didnotReceiveTypho}
          >
            {strings.displayText.notreceiveOtp}
          </Typography>
          <Button
            onClick={handleResendButton}
            disabled={counter !== 0}
            variant="text"
          >
            <Typography component="h5" variant="h5" sx={styles.resendOtpTypho}>
              {strings.displayText.resendOtp}
            </Typography>
          </Button>
          {counter !== 0 ? (
            <Typography component="h5" variant="h5" sx={styles.resendOtpTypho}>
              {counter}
            </Typography>
          ) : null}
        </Box>

        <LoadingButton
          onClick={handleVerifyOtp}
          type="submit"
          fullWidth
          variant="contained"
          loading={loader}
          loadingPosition="end"
          sx={styles.loadingBtn}
        >
          <Typography component="h1" variant="h5">
            {strings.displayText.verifyOtp}
          </Typography>
        </LoadingButton>
      </Box>
    </Fragment>
  );
};

export default VerifyOtp;
